import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';

import { countries } from '../../shared/constants/country.constant';
import { States } from '../../shared/constants/states.constant';
import {
    RideShare,
    RideShareEmployer,
    RideShareStatus,
} from '../../shared/constants/ride-share.constant';
import { UberStatus } from '../../shared/constants/uber-status.constant';
import { VehicleOwnership } from '../../shared/constants/vehicle-ownership.constant';
import { DialogHandlerService } from '../../core/services/dialog-handler/dialog-handler.service';
import { JoinService } from '../../core/services/join/join.service';
import { catchError } from 'rxjs';
import { Router } from '@angular/router';
import {
    startUserSessionMonitor,
    resetUserToken,
} from '../../shared/utitlity-functions/utility-functions';
import { environment } from '../../../environments/environment';
import { RoutePaths } from '../../shared/constants/route-paths.constant';

@Component({
    standalone: false,
    selector: 'octo-reapply',
    templateUrl: './reapply.component.html',
    styleUrls: ['./reapply.component.scss'],
})
export class ReapplyComponent implements OnInit {
    reapplyForm: any;
    country: any = '';
    countries = countries;
    currentUser: any;
    availableStates: any = States;
    submitted: boolean = false;
    submitting: boolean = false;
    businessError: boolean = false;
    states = [];
    rideshareEmployerOptions = RideShareEmployer;
    rideShares = RideShare;
    rideshareStatusOptions = RideShareStatus;
    uberStatusOptions = UberStatus;
    vehicleOwnershipOptions = VehicleOwnership;
    isPlasticHasValue: boolean = false;
    zipPattern: string = '';

    octoGooglePlacesAutocomplete = {
        googleAddressOptions: {
            componentRestrictions: { country: 'US' },
        },
        formGroup: {},
    };

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly cdref: ChangeDetectorRef,
        private readonly dialogService: DialogHandlerService,
        private readonly joinService: JoinService,
        private readonly router: Router
    ) { }

    ngOnInit(): void {
        resetUserToken();
        startUserSessionMonitor();

        this.initializeForm();
        this.reapplyForm.controls[
            'has_plastic_partition'
        ].valueChanges.subscribe((val: any) => {
            if (val === '') {
                this.isPlasticHasValue = false;
            } else {
                this.isPlasticHasValue = true;
            }
        });
    }

    get form() {
        return this.reapplyForm.controls;
    }

    setFormgroup(type: any): void {
        this.octoGooglePlacesAutocomplete.formGroup = this.reapplyForm.get(
            type
        ) as UntypedFormGroup;
    }

    get address_data(): UntypedFormGroup {
        return this.reapplyForm.get('address_data') as UntypedFormGroup;
    }

    initializeForm() {
        this.reapplyForm = this.fb.group({
            phone_number: [
                '',
                [
                    Validators.pattern(/^\([2-9]\d{2}\)\s\d{3}-\d{4}$/),
                    Validators.required,
                ],
            ],
            address_data: this.fb.group({
                address_line: [null, Validators.required],
                address_apartment_suite_no: [
                    null,
                    Validators.pattern('^((?!#).)*$'),
                ],
                address_city: [null, Validators.required],
                address_state: [null, Validators.required],
                address_zip: [null, [Validators.required]],
                address_country: [this.country, Validators.required],
            }),
            primary_rideshare_employer: [null, Validators.required],
            secondary_rideshare_employer: [null],
            uber_status: [null],
            vehicle_ownership: ['', Validators.required],
            rideshare_status: ['', Validators.required],
            has_plastic_partition: [''],
            reapply_reason: ['', Validators.required],
            vehicle_ownership_other: [''],
            lifetime_rides: [
                '',
                [Validators.required, Validators.pattern(String.raw`^\d{1,6}$`)],
            ],
            has_nyctlc_license: [false],
            agree: [false, Validators.requiredTrue],
            rides_agree: [false, Validators.requiredTrue],
        });

        this.octoGooglePlacesAutocomplete.formGroup = this.reapplyForm.get(
            'address_data'
        ) as UntypedFormGroup;
        this.getInitialData();
    }

    setPattern() {
        const zip = this.reapplyForm?.get('address_data')?.get('address_zip');
        if (this.country == 'US') {
            this.zipPattern = String.raw`^(\d{5}(-\d{4})?|\d+$ *\d[A-Z]\d)$`;
        } else {
            this.zipPattern = String.raw`^([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$`;
        }
        if (zip) {
            zip.setValidators([
                Validators.required,
                Validators.pattern(this.zipPattern),
            ]);
            zip.updateValueAndValidity();
        }
        this.cdref.detectChanges();
    }

    getInitialData() {
        this.joinService.getJoinDetails().subscribe((res: any) => {
            this.currentUser = res.data;
            let data = res.data;
            this.joinService.setUserData(res.data);
            localStorage.setItem('status', res.data.status);
            if (this.currentUser.market == 'New York, NY') {
                this.reapplyForm
                    .get('has_nyctlc_license')
                    .setValidators(Validators.required);
                this.reapplyForm.get('has_nyctlc_license')
                    .updateValueAndValidity();
                this.cdref.detectChanges();
            }
            let temp = {
                driver_info: {
                    primary_rideshare_employer:
                        data?.driver_info.primary_rideshare_employer,
                    secondary_rideshare_employer:
                        data?.driver_info.secondary_rideshare_employer,
                    vehicle_ownership: data?.driver_info.vehicle_ownership,
                    vehicle_ownership_other:
                        data?.driver_info.vehicle_ownership_other,
                    has_plastic_partition:
                        data?.driver_info.has_plastic_partition,
                    uber_status: data?.driver_info.uber_status,
                    rideshare_status: data?.driver_info.rideshare_status,
                },
            };
            this.country = data.shipping_info.mailing_address_country;
            if (this.country == 'US') {
                this.zipPattern = String.raw`^(\d{5}(-\d{4})?|\d+$ *\d[A-Z]\d)$`;
            } else {
                this.zipPattern = String.raw`^([A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$`;
            }
            setTimeout(() => {
                this.setPattern();
            }, 1);
            this.octoGooglePlacesAutocomplete = {
                googleAddressOptions: {
                    componentRestrictions: { country: this.country },
                },
                formGroup: this.reapplyForm.get(
                    'address_data'
                ) as UntypedFormGroup,
            };

            if (!data?.driver_info.rideshare_status)
                temp.driver_info.rideshare_status = '';
            if (!data?.driver_info.uber_status)
                temp.driver_info.uber_status = '';
            if (
                !data?.driver_info.primary_rideshare_employer ||
                !this.rideshareEmployerOptions.some(function (el) {
                    return (
                        el.value === temp.driver_info.primary_rideshare_employer
                    );
                })
            ) {
                temp.driver_info.primary_rideshare_employer = '';
            }
            if (
                !data?.driver_info.secondary_rideshare_employer ||
                !this.rideshareEmployerOptions.some(function (el) {
                    return (
                        el.value ===
                        temp.driver_info.secondary_rideshare_employer
                    );
                })
            )
                temp.driver_info.secondary_rideshare_employer = '';
            if (!data?.driver_info.vehicle_ownership)
                temp.driver_info.vehicle_ownership = '';
            else if (
                data?.driver_info.vehicle_ownership.includes('-') &&
                data?.driver_info.vehicle_ownership.includes('other')
            ) {
                temp.driver_info.vehicle_ownership_other =
                    data?.driver_info.vehicle_ownership.split('-')[1];
                temp.driver_info.vehicle_ownership = 'other';
                this.onVehicleOwnerShipChange();
            }
            if (data?.driver_info.has_plastic_partition === null) {
                temp.driver_info.has_plastic_partition = '';
                this.isPlasticHasValue = false;
            } else {
                this.isPlasticHasValue = true;
            }
            this.states = this.availableStates[this.country];

            this.reapplyForm.patchValue({
                ...data?.driver_info,
                ...temp?.driver_info,
                phone_number: data?.phone_number,
                address_data: {
                    address_line: data?.shipping_info.mailing_address_line,
                    address_apartment_suite_no:
                        data?.shipping_info.mailing_address_apartment_suite_no,
                    address_city: data?.shipping_info.mailing_address_city,
                    address_state: data?.shipping_info.mailing_address_state,
                    address_zip: data?.shipping_info.mailing_address_zip,
                    address_country:
                        data?.shipping_info.mailing_address_country,
                },
            });
            this.cdref.detectChanges();
        });
    }

    markTouched(control: any): void {
        this.reapplyForm.controls[control].markAsTouched();
    }

    onVehicleOwnerShipChange(): void {
        const vehicleOwnerShipOther = this.reapplyForm.get(
            'vehicle_ownership_other'
        );
        if (this.reapplyForm?.value.vehicle_ownership === 'other') {
            vehicleOwnerShipOther.setValidators(Validators.required);
        } else {
            vehicleOwnerShipOther.clearValidators();
            vehicleOwnerShipOther.setErrors(null);
        }
        vehicleOwnerShipOther.updateValueAndValidity();
        this.cdref.detectChanges();
    }

    rideShareUpdated(): void {
        const uberStatusField = this.reapplyForm.get('uber_status');
        const { primary_rideshare_employer, secondary_rideshare_employer } =
            this.reapplyForm.value;
        if (
            primary_rideshare_employer?.toLowerCase?.().includes('uber') ||
            secondary_rideshare_employer?.toLowerCase?.().includes('uber')
        ) {
            uberStatusField.setValidators(Validators.required);
        } else {
            uberStatusField.clearValidators();
        }
        uberStatusField.updateValueAndValidity();
    }

    submitInfo(): void {
        this.submitted = true;
        if (!this.reapplyForm.valid) {
            return;
        }
        this.submitting = true;
        this.fedexAddressValidation();
    }

    showTermsModal(type: string): void {
        this.dialogService.showTermsModal(type).subscribe({});
    }

    fedexAddressValidation(): void {
        if (!this.reapplyForm.valid) {
            return;
        }

        const {
            address_line,
            address_apartment_suite_no: apartment,
            address_city,
            address_state,
            address_country,
            address_zip,
        } = this.reapplyForm.value.address_data;

        let payloadData = {
            address_line: address_line ? address_line.replace(/[#]/gi, '') : '',
            address_apartment_suite_no: apartment
                ? apartment.replace(/[#]/gi, '')
                : '',
            address_city: address_city,
            address_state: address_state,
            address_country: address_country,
            address_zip: address_zip,
        };

        let address = address_line;
        if (apartment) {
            address = `${address}, ${apartment}`;
        }
        address = `${address}, ${address_city}, ${address_state}, ${address_country} - ${address_zip}`;
        let poExists = false;
        const setOfStrings = ['po box', 'p.o. box', 'p.o.box'];
        const addressLine = address_line.toLowerCase();
        const apartmentSuiteNo = apartment ? apartment.toLowerCase() : '';
        poExists = setOfStrings.some(
            (item) =>
                addressLine.includes(item) || apartmentSuiteNo.includes(item)
        );
        if (poExists) {
            let fields = [];
            fields.push({
                type: 'hidden',
                name: 'address',
                value: payloadData,
                label: address,
                head: 'You Entered:',
            });
            this.dialogService
                .showFedexAddressSuggestion({
                    title: 'Warning',
                    fields: fields,
                    hasSuggestions: false,
                    errorMessage: 'Note: We cannot ship to P.O. boxes',
                    isRadio: true,
                })
                .subscribe((data) => this.updateAddress(data));
            return;
        }
        this.joinService
            .getFedExSuggestion(payloadData)
            .pipe(
                catchError((error: any) => {
                    throw new Error('Error');
                })
            )
            .subscribe({
                next: (resp) => {
                    let suggestedAddresses: any = [];
                    let addressError = '';
                    let fedexSuggestion = resp.data;
                    let isSameAddress = false;
                    let showInvalidAptNote = false;
                    let isSuiteNumberRequired = false;
                    let isStreetNumberIncorrect = false;
                    let hasSuggestions = false;
                    let businessAddress = false;

                    if (!resp.success && !fedexSuggestion?.length) {
                        suggestedAddresses.push({
                            type: 'radio',
                            name: 'address',
                            value: payloadData,
                            label: address,
                            head: 'You Entered:',
                        });
                        hasSuggestions = false;
                        addressError =
                            'Warning! We could not find any addresses that match this entry.';
                        this.dialogService
                            .showFedexAddressSuggestion({
                                title: 'Warning!',
                                fields: suggestedAddresses,
                                hasSuggestions: hasSuggestions,
                                errorMessage: addressError,
                                isRadio: true,
                            })
                            .subscribe((data: any) => this.updateAddress(data));
                        return;
                    }
                    if (resp.success && !fedexSuggestion?.length) {
                        this.submitData();
                    } else {
                        fedexSuggestion.forEach((addr: any, i: number) => {
                            const vL1Changed =
                                addr.street_line !== address_line;
                            const vL2Changed =
                                apartment && apartment !== addr.apartment_line;
                            if (
                                addr.is_resolved &&
                                !addr.po_box &&
                                addr.address_type === 'STANDARDIZED'
                            ) {
                                let addressRow = `<span ${vL1Changed ? `class='highlight'` : ''
                                    }>${addr.street_line},</span>
                                        <span ${vL2Changed
                                        ? `class='highlight'`
                                        : ''
                                    }>${addr.apartment_line
                                        ? addr.apartment_line + ', '
                                        : ''
                                    }${addr.is_invalid_suite_number
                                        ? '<sup>*</sup>'
                                        : ''
                                    }</span>
                                        <span ${addr.is_city_changed
                                        ? `class='highlight'`
                                        : ''
                                    }>${addr.city}, </span>
                                        <span ${addr.is_state_changed
                                        ? `class='highlight'`
                                        : ''
                                    }>${addr.state}, </span>
                                        <span>${addr.country} - </span>
                                        <span ${addr.is_zip_changed
                                        ? `class='highlight'`
                                        : ''
                                    }>${addr.zip_code}</span>`;

                                if (
                                    !showInvalidAptNote &&
                                    addr.is_invalid_suite_number
                                ) {
                                    showInvalidAptNote = true;
                                }
                                if (
                                    !businessAddress &&
                                    addr.classification === 'BUSINESS'
                                ) {
                                    businessAddress = true;
                                }
                                if (
                                    (!isSuiteNumberRequired &&
                                        addr.error_messages.includes(
                                            'SUITE.NUMBER.REQUIRED'
                                        )) ||
                                    addr.error_messages.includes(
                                        'INVALID.SUITE.NUMBER'
                                    )
                                ) {
                                    isSuiteNumberRequired = true;
                                }
                                if (
                                    !isStreetNumberIncorrect &&
                                    addr.error_messages.includes(
                                        'INTERPOLATED.STREET.ADDRESS'
                                    )
                                ) {
                                    isStreetNumberIncorrect = true;
                                }
                                if (
                                    !isSameAddress &&
                                    !vL1Changed &&
                                    !vL2Changed &&
                                    !addr.is_city_changed &&
                                    !addr.is_state_changed &&
                                    !addr.is_zip_changed
                                ) {
                                    isSameAddress = true;
                                    if (fedexSuggestion.length == 1) {
                                        this.submitData();
                                        return;
                                    } else return;
                                }

                                //Add entered address in the address suggestion modal
                                suggestedAddresses.push({
                                    type:
                                        isStreetNumberIncorrect ||
                                            businessAddress
                                            ? 'hidden'
                                            : 'radio',
                                    name: 'address',
                                    value: payloadData,
                                    label: address,
                                    head: 'You Entered:',
                                });
                                if (
                                    !isStreetNumberIncorrect &&
                                    !businessAddress
                                ) {
                                    suggestedAddresses.push({
                                        head: i === 0 ? 'We Suggest' : '',
                                        type: 'radio',
                                        name: 'address',
                                        value: {
                                            address_line: addr.street_line
                                                ? addr.street_line.replace(
                                                    /[#]/gi,
                                                    ''
                                                )
                                                : '',
                                            address_apartment_suite_no:
                                                addr.apartment_line
                                                    ? addr.apartment_line.replace(
                                                        /[#]/gi,
                                                        ''
                                                    )
                                                    : '',
                                            address_city: addr.city,
                                            address_state: addr.state,
                                            address_country: addr.country,
                                            address_zip: addr.zip_code,
                                        },
                                        label: addressRow,
                                    });
                                    hasSuggestions = true;
                                } else {
                                    addressError = businessAddress
                                        ? 'Warning: We do not ship to commercial addresses. Please make sure the address is a residential address.'
                                        : 'Warning! We could not find any addresses that match this entry.';
                                    hasSuggestions = false;
                                }
                            } else {
                                suggestedAddresses.push({
                                    type: 'hidden',
                                    name: 'address',
                                    value: payloadData,
                                    label: address,
                                    head: 'You Entered:',
                                });
                                hasSuggestions = false;
                                if (
                                    !businessAddress &&
                                    addr.classification === 'BUSINESS'
                                ) {
                                    businessAddress = true;
                                }
                                if (addr.po_box) {
                                    addressError = 'Note: We cannot ship to P.O. boxes';
                                } else if (businessAddress) {
                                    addressError = 'Warning: We do not ship to commercial addresses. Please make sure the address is a residential address.';
                                } else {
                                    addressError = 'Warning! We could not find any addresses that match this entry.';
                                }
                            }
                        });
                        if (addressError || suggestedAddresses.length) {
                            if (hasSuggestions) {
                                this.dialogService
                                    .showFedexAddressSuggestion({
                                        title: 'Warning!',
                                        fields: suggestedAddresses,
                                        hasSuggestions: hasSuggestions,
                                        errorMessage: addressError,
                                    })
                                    .subscribe((data: any) =>
                                        this.updateAddress(data)
                                    );
                            } else if (businessAddress) {
                                this.dialogService
                                    .showContinueWithoutFedEx({
                                        title: 'Warning!',
                                        fields: suggestedAddresses,
                                        hasSuggestions: false,
                                        withoutValidation: false,
                                        errorMessage: addressError,
                                        isRadio: true,
                                    })
                                    .subscribe((data: any) =>
                                        this.updateAddress(data)
                                    );
                            } else {
                                this.dialogService
                                    .showContinueWithoutFedEx({
                                        title: 'Warning!',
                                        fields: suggestedAddresses,
                                        hasSuggestions: false,
                                        withoutValidation: false,
                                        errorMessage: addressError,
                                        isRadio: true,
                                    })
                                    .subscribe((data: any) =>
                                        this.updateAddress(data)
                                    );
                            }
                            return;
                        }
                    }
                },
                error: (error) => {
                    let suggestedAddresses: any = [];
                    suggestedAddresses.push({
                        type: 'radio',
                        name: 'address',
                        value: payloadData,
                        label: address,
                        head: 'You Entered:',
                    });
                    this.dialogService
                        .showFedexAddressSuggestion({
                            title: 'Warning!',
                            fields: suggestedAddresses,
                            hasSuggestions: false,
                            errorMessage:
                                'Warning! We could not find any addresses that match this entry.',
                            isRadio: true,
                        })
                        .subscribe((data) => this.updateAddress(data));
                    return;
                },
            });
    }
    updateAddress(data: any) {
        if (data.selectedAddress) {
            this.reapplyForm.patchValue({
                address_data: { ...data.selectedAddress },
            });
            this.submitData();
        } else {
            this.reapplyForm.patchValue({
                address_data: {
                    address_line: this.currentUser?.shipping_info
                        ?.mailing_address_line
                        ? this.currentUser?.shipping_info.mailing_address_line?.toUpperCase()
                        : '',
                    address_apartment_suite_no: this.currentUser?.shipping_info
                        ?.mailing_address_apartment_suite_no
                        ? this.currentUser?.shipping_info.mailing_address_apartment_suite_no?.toUpperCase()
                        : '',
                    address_state:
                        this.currentUser.shipping_info?.mailing_address_state,
                    address_city:
                        this.currentUser.shipping_info?.mailing_address_city,
                    address_zip:
                        this.currentUser.shipping_info?.mailing_address_zip,
                    address_country:
                        this.currentUser.shipping_info?.mailing_address_country,
                },
            });
            this.submitting = false;
        }
    }
    submitData() {
        let payload = {
            ...this.reapplyForm.value,
            ...this.reapplyForm.value.address_data,
        };
        payload['has_plastic_partition'] == 'true'
            ? (payload['has_plastic_partition'] = true)
            : (payload['has_plastic_partition'] = false);
        if (payload['uber_status'] == '') payload['uber_status'] = null;
        if (payload['secondary_rideshare_employer'] == '')
            payload['secondary_rideshare_employer'] = null;
        delete payload['address_data'];
        delete payload['agree'];
        delete payload['rides_agree'];
        payload['phone_number'] = payload['phone_number'].replace(
            /[- )(]/g,
            ''
        );
        this.joinService.saveReapplyDetails(payload).subscribe({
            next: (res) => {
                if (res.data.out_of_bounds) {
                    this.openModal(res.data);
                } else {
                    this.getJoinDetails(res.data);
                }
            },
            error: (error: any) => {
                this.submitting = false;
            },
        });
    }
    openModal(res: any) {
        const addressCountry = this.reapplyForm?.value.address_data.address_country?.toLowerCase();
        const country = addressCountry === 'ca' ? 80 : 50;
        this.dialogService
            .confirmAction(
                {
                    title: 'Warning!',
                    isPinkTitle: true,
                    message: `To receive a tablet, you must complete the majority of your trips within ${country} miles of an active Octopus market.`,
                },
                false
            )
            .subscribe((data: any) => {
                this.getJoinDetails(res);
            });
    }

    getJoinDetails(res: any) {
        const statusRoute: any = {
            payment_complete: environment.ENFORCE_UBER_CONNECT_FLAG ? RoutePaths.UBER_CONNECT_REQUIRED : RoutePaths.PROOF_OF_RIDES,
            interested_ready_for_tablet: RoutePaths.READY_FOR_TABLET,
        };
        localStorage.setItem('status', res.status);
        this.joinService.getJoinDetails().subscribe((data) => {
            this.joinService.setUserData(data?.data);
            this.router.navigate([statusRoute[res.status]]);
        });
    }
}
